import { getApiUrl } from "../lib/api/config";

/**
 * Submit tax prep "Digital Shoebox" data to the backend.
 * - When cpaId is present: backend emails the CPA with Schedule E and CC's the user.
 * - When sendToUserOnly is true (no valid CPA link): backend sends an informational email
 *   only to the user with the Schedule E Excel attachment.
 *
 * @param {Object} payload - The tax prep submission payload
 * @param {string} [payload.cpaId] - CPA identifier when sending to a CPA (e.g., 'payton')
 * @param {boolean} [payload.sendToUserOnly] - When true, email only the user with Schedule E (no CPA)
 * @param {string} payload.userName - User's full name
 * @param {string} payload.userEmail - User's email (recipient when sendToUserOnly; CC when sending to CPA)
 * @param {string[]} payload.properties - Array of property addresses
 * @param {string} [payload.propertyAddress] - First property address (for backend compatibility)
 * @param {number} payload.totalRentCollected - Total rents collected
 * @param {Array<{amount: number, category: string}>} payload.expenses - Array of expense rows
 * @returns {Promise<{success: boolean, message?: string}>}
 */

/** Extract error message from various backend response shapes */
function getErrorMessage(result, response) {
  if (result?.message) return result.message;
  if (result?.error) return result.error;
  if (result?.detail)
    return typeof result.detail === "string"
      ? result.detail
      : JSON.stringify(result.detail);
  if (Array.isArray(result?.errors) && result.errors.length > 0) {
    return result.errors.map((e) => e.message || e.msg || e).join(". ");
  }
  if (!response.ok) {
    return `Request failed (${response.status}). Please try again or contact support.`;
  }
  return null;
}

export async function submitTaxPrep(payload) {
  const baseUrl = getApiUrl().replace(/\/$/, "");
  const route = "/tax-prep/submit";
  const url = `${baseUrl}${route}`;

  if (process.env.NODE_ENV === "development") {
    console.log("[Tax Prep API] Request:", { url, method: "POST", payload });
  }

  const response = await fetch(url, {
    method: "POST",
    headers: {
      accept: "application/json",
      "content-type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  let result;
  try {
    const text = await response.text();
    result = text ? JSON.parse(text) : {};
    if (process.env.NODE_ENV === "development") {
      console.log("[Tax Prep API] Response:", {
        status: response.status,
        ok: response.ok,
        result,
      });
    }
  } catch (parseError) {
    console.error("Tax prep response parse error:", parseError);
    return {
      success: false,
      message: response.ok
        ? "Invalid response from server."
        : `Request failed (${response.status}). Please try again or contact support.`,
    };
  }

  if (!result.success && !result.message) {
    result.message = getErrorMessage(result, response);
  }
  return result;
}
